// HTML cluster bubbles for the onshore layers (same diff-on-render pattern as
// farmClusters.ts), brand-styled mint/Deep.
//
//   Landanlegg    → bubble sized by Σ wellboat traffic (summed from the actual
//                   cluster leaves — reliable regardless of cluster-property
//                   quirks — and cached per cluster id).
//   Servicehavner → bubble sized by harbour COUNT (built-in point_count).
//
// Both expand on click via getClusterExpansionZoom.

import maplibregl from "maplibre-gl";

import { filletGlyph } from "./onshore";
import { anchorGlyph } from "./serviceports";
import { anchorCoord, clearAnchorCache } from "./clusterAnchor";

const DEEP = "#082E22";
const MINT = "#B4D9C7";

// Each layer's ISOTYPE, sized to sit inside the cluster bubble. The fillet's
// striations / the anchor strokes punch back to the bubble fill so the glyph
// reads against either the mint (Landanlegg) or Deep (Servicehavner) ground.
const FILLET = filletGlyph(DEEP, MINT); // Deep glyph on mint bubble
const ANCHOR = anchorGlyph(MINT); // mint glyph on Deep bubble

function svgWrap(inner: string, px: number): string {
  return `<svg width="${px}" height="${px}" viewBox="-22 -22 44 44" aria-hidden="true">${inner}</svg>`;
}

function clamp(lo: number, hi: number, v: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// Bubbles encode magnitude by SIZE only — no number inside, just the layer's
// ISOTYPE so the kind is identifiable. The eye reads "where is the activity"
// from the relative diameters; exact figures live in the per-site popups.
type Val = { d: number; glyph: string };
type ValueFor = (props: Record<string, unknown>) => Val;

const registries: Record<
  string,
  { markers: Record<string, maplibregl.Marker>; onScreen: Record<string, maplibregl.Marker> }
> = {};

function styleBubble(el: HTMLElement, v: Val): void {
  el.style.width = `${v.d}px`;
  el.style.height = `${v.d}px`;
  el.innerHTML = svgWrap(v.glyph, Math.round(v.d * 0.56));
}

function sync(map: maplibregl.Map, source: string, cls: string, valueFor: ValueFor): void {
  if (!map.getSource(source) || !map.isSourceLoaded(source)) return;
  const reg = registries[source] ?? (registries[source] = { markers: {}, onScreen: {} });
  const next: Record<string, maplibregl.Marker> = {};
  for (const f of map.querySourceFeatures(source)) {
    const props = f.properties as Record<string, unknown>;
    if (!props.cluster) continue;
    const id = String(props.cluster_id);
    const centroid = (f.geometry as GeoJSON.Point).coordinates as [number, number];
    // Anchor to a real member, not the centroid (which can land on land / at sea).
    const coords = anchorCoord(map, source, Number(props.cluster_id), centroid);
    const v = valueFor(props);
    let marker = reg.markers[id];
    if (!marker) {
      const el = document.createElement("div");
      el.className = cls;
      styleBubble(el, v);
      el.addEventListener("click", () => {
        const src = map.getSource(source) as maplibregl.GeoJSONSource | undefined;
        src?.getClusterExpansionZoom(Number(props.cluster_id)).then((zoom) => {
          map.easeTo({ center: coords, zoom: zoom + 0.2, duration: 500 });
        });
      });
      marker = reg.markers[id] = new maplibregl.Marker({ element: el }).setLngLat(coords);
    } else {
      styleBubble(marker.getElement(), v);
      marker.setLngLat(coords); // reposition once the real anchor resolves
    }
    next[id] = marker;
    if (!reg.onScreen[id]) marker.addTo(map);
  }
  for (const id in reg.onScreen) if (!next[id]) reg.onScreen[id]?.remove();
  reg.onScreen = next;
}

function reset(source: string): void {
  clearAnchorCache(source);
  const reg = registries[source];
  if (!reg) return;
  for (const id in reg.onScreen) reg.onScreen[id]?.remove();
  registries[source] = { markers: {}, onScreen: {} };
}

// --- Landanlegg: size by summed traffic (computed from cluster leaves) ------

const sumCache = new Map<number, number>();
const pending = new Set<number>();

export function updateOnshoreClusters(map: maplibregl.Map): void {
  sync(map, "onshore", "onshore-bubble", (p) => {
    const cid = Number(p.cluster_id);
    let total = sumCache.get(cid);
    if (total === undefined) {
      total = Number(p.tsum) || 0; // best-effort until the leaves resolve
      if (!pending.has(cid)) {
        pending.add(cid);
        const src = map.getSource("onshore") as maplibregl.GeoJSONSource | undefined;
        src
          ?.getClusterLeaves(cid, 1000, 0)
          .then((leaves) => {
            const s = leaves.reduce(
              (a, l) => a + (Number((l.properties as Record<string, unknown>)?.traffic) || 0),
              0,
            );
            sumCache.set(cid, s);
            pending.delete(cid);
            map.triggerRepaint(); // re-render so the bubble picks up the real sum
          })
          .catch(() => pending.delete(cid));
      }
    }
    // sqrt → area tracks total traffic; wide clamp so quiet vs. busy is obvious.
    return { d: clamp(30, 100, 22 + Math.sqrt(total) * 4.5), glyph: FILLET };
  });
}
export function resetOnshoreClusters(): void {
  reset("onshore");
  sumCache.clear();
  pending.clear();
}

// --- Servicehavner: size by harbour count ----------------------------------

export function updateServiceClusters(map: maplibregl.Map): void {
  sync(map, "serviceports", "onshore-bubble service-bubble", (p) => {
    const c = Number(p.point_count) || 0;
    return { d: clamp(28, 84, 22 + Math.sqrt(c) * 8), glyph: ANCHOR };
  });
}
export function resetServiceClusters(): void {
  reset("serviceports");
}

// --- Havner / driftstype: size by harbour count -----------------------------

export function updatePortsClusters(map: maplibregl.Map): void {
  sync(map, "ports", "onshore-bubble service-bubble", (p) => {
    const c = Number(p.point_count) || 0;
    return { d: clamp(28, 90, 22 + Math.sqrt(c) * 6), glyph: ANCHOR };
  });
}
export function resetPortsClusters(): void {
  reset("ports");
}
